
// Libraries
import { getFormattedLabel } from "@/operations/screensizes";
import { Chart, registerables, Tooltip } from "chart.js";
import { CrosshairPlugin } from "chartjs-plugin-crosshair";
import gradient from "chartjs-plugin-gradient";
import dayjs from "dayjs";
import { Component, Prop, Vue, Watch } from "nuxt-property-decorator";
// Components
import LoadingSpinner from "~/components/molecules/display/LoadingSpinner.vue";

@Component({
  components: {
    LoadingSpinner,
  },
})
export default class PelletsChartComponent extends Vue {
  @Prop({ required: true }) canvasID: string;
  @Prop() chartDescription: string;
  @Prop() yAchsesLabel: string;
  @Prop({ default: false }) fullWidth: boolean;
  @Prop() chartDataLabels: Array<string | number>;
  @Prop({ default: "Sackware" }) mainChartDataLabel: string;
  @Prop() mainChartDataSeries: Array<string>;
  @Prop({ default: "Lose Ware" }) comparisonChartDataLabel: string;
  @Prop({ default: false }) isCityChart: boolean;
  @Prop() comparisonChartDataSeries: Array<string>;

  chartInstance: any;

  get hasData() {
    return (
      this.mainData.length > 0 ||
      this.compareData.length > 0 ||
      this.labels.length > 0
    );
  }

  get tooltipValueElements() {
    return [
      document.getElementById("tooltip-value-0"),
      document.getElementById("tooltip-value-1"),
    ];
  }

  get tooltipColorElements() {
    return [
      document.getElementById("tooltip-color-0"),
      document.getElementById("tooltip-color-1"),
    ];
  }

  // Chart aspect ratio based on screen size
  get chartAspectRatio(): number {
    if (this.fullWidth) {
      if (window.innerWidth > 1100) {
        return 3.2;
      } else return window.innerWidth < 700 ? 1.5 : 2.5;
    } else {
      if (window.innerWidth > 1100) {
        return 2;
      } else return window.innerWidth < 700 ? 1.5 : 2.5;
    }
  }

  getTooltip = (chart) => {
    let tooltip = document.getElementById("chart-tooltip");
    let tooltipTitle = tooltip.querySelector(".chart__tooltip-title");

    tooltipTitle.textContent = chart.tooltip.title;

    if (chart.tooltip.x !== undefined) {
      this.tooltipValueElements.forEach((element, index) => {
        element.textContent =
          chart.tooltip.dataPoints[index] === undefined
            ? ""
            : `${chart.tooltip.body[index].lines[0]}`;
      });
    }

    tooltip.style.top = chart.tooltip.y + 40 + "px";

    const xAlignLeft = window.innerWidth >= 680 ? 45 : 10;
    let xOffset;

    // Determine x position offset based on tooltip alignment
    if (
      (chart.tooltip.xAlign === "left" && chart.tooltip.yAlign === "bottom") ||
      (chart.tooltip.xAlign === "left" && chart.tooltip.yAlign === "top")
    ) {
      xOffset = xAlignLeft + 20;
    } else if (chart.tooltip.xAlign === "left") {
      xOffset = xAlignLeft;
    } else if (chart.tooltip.xAlign === "center") {
      xOffset = -95;
    } else {
      xOffset = -40;
    }

    tooltip.style.left = chart.tooltip.x + xOffset + "px";

    return tooltip;
  };

  externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;

    if (context.tooltip.x !== undefined) {
      this.tooltipColorElements.forEach((element, index) => {
        element.style.backgroundColor =
          context.tooltip.labelColors[index] === undefined
            ? "transparent"
            : context.tooltip.labelColors[index].borderColor;
      });
    }

    const tooltipEl = this.getTooltip(chart);

    if (tooltip.opacity === 0 || tooltip.body[0]?.lines[0] === undefined) {
      tooltipEl.style.opacity = "0";
      tooltipEl.style.pointerEvents = "none";
    } else {
      tooltipEl.style.opacity = "1";
      tooltipEl.style.pointerEvents = "auto";
    }
  };

  get chartConfig() {
    return {
      type: "line",
      data: {
        labels: this.chartDataLabels,
        datasets: [
          {
            label: this.comparisonChartDataLabel,
            data: this.comparisonChartDataSeries,
            borderColor: "rgba(0, 0, 0, 1)",
            backgroundColor: function (context) {
              const chart = context.chart;
              const { ctx, chartArea } = chart;

              if (!chartArea) {
                return null;
              }

              // Create gradient for the grey line (bottom to top)
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top,
              );
              gradient.addColorStop(0, "rgba(0, 0, 0, 0.00)"); // Transparent at bottom
              gradient.addColorStop(1, "rgba(0, 0, 0, 0.12)"); // Grey at top (0%)

              return gradient;
            },
            borderWidth: 2.5,
            pointBorderColor: "rgba(0, 0, 0, 1)",
            pointBackgroundColor: "rgba(0, 0, 0, 1)",
            pointRadius: 0,
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
            font: {
              size: "20",
            },
            fill: true,
            tension: 0.4,
          },
          {
            label: this.mainChartDataLabel,
            data: this.mainChartDataSeries,
            borderColor: "rgba(253, 200, 53, 1)",
            backgroundColor: function (context) {
              const chart = context.chart;
              const { ctx, chartArea } = chart;

              if (!chartArea) {
                return null;
              }

              // Create gradient for the yellow line (bottom to top)
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top,
              );
              gradient.addColorStop(0, "rgba(253, 200, 53, 0.00)"); // Transparent at 47.5%
              gradient.addColorStop(0.525, "rgba(253, 200, 53, 0.00)"); // Transparent at 47.5%
              gradient.addColorStop(1, "rgba(253, 200, 53, 0.40)"); // Yellow at top (0%)

              return gradient;
            },
            borderWidth: 2.5,
            pointBorderColor: "rgba(253, 200, 53, 1)",
            pointBackgroundColor: "rgba(253, 200, 53, 1)",
            pointRadius: 0,
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
            fill: true,
          },
        ],
      },
      options: {
        aspectRatio: this.chartAspectRatio,
        responsive: true,
        lineTension: 1,
        spanGaps: true,
        elements: {
          line: {
            cubicInterpolationMode: "monotone",
          },
        },
        title: {
          display: false,
          fontSize: 22,
          position: "bottom" as const,
        },
        legend: {
          display: true,
          position: "bottom" as const,
          align: "center" as const,
          labels: {
            fontColor: "rgba(0,0,0)",
            usePointStyle: true,
            boxWidth: 10,
            padding: 20,
          },
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        scales: {
          y: {
            position: "right",
            beginAtZero: true,
            ticks: {
              beginAtZero: false,
              maxTicksLimit: 6,
              stepSize: 2,
              font: {
                size: this.fullWidth ? 16 : 13,
                family: "Fira Sans",
              },
              color: "#7d7d7d",
              callback(value, index, values) {
                return `${value} €`;
              },
            },
            scaleLabel: {
              display: false,
              labelString: "€ / 1000 kg",
              fontColor: "#000000",
              fontSize: 12,
            },
            grid: {
              color: "rgba( 211, 211, 219, 0.4 )",
              drawBorder: false,
              drawOnChartArea: true,
              drawTicks: true,
              tickMarkLength: 10,
            },
          },
          x: {
            type: "category",
            distribution: "linear" as const,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 3,
              maxRotation: 0,
              font: {
                size: this.fullWidth ? 16 : 13,
                family: "Fira Sans",
              },
              color: "#7d7d7d",
              align: "center",
              callback: (context) => {
                const splittedDate = this.chartDataLabels[context]
                  .toString()
                  .split(".");

                return dayjs(
                  `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`,
                ).format("MMM");
              },
            },
            scaleLabel: {
              display: false,
              labelString: "",
              fontColor: "#000000",
              fontSize: 12,
            },
            grid: {
              color: "rgba( 211, 211, 219, 0.4 )",
              zeroLineColor: "#cbd0d2",
              zeroLineBorderDash: [3, 9],
              drawBorder: true,
              drawOnChartArea: true,
              drawTicks: true,
              tickMarkLength: 10,
            },
          },
        },

        plugins: {
          tooltip: {
            enabled: false,
            external: this.externalTooltipHandler,
            position: "custom",
            mode: "index",
            intersect: false,
            displayColors: true,
            xPadding: 20,
            yPadding: 20,
            caretPadding: 6,
            cornerRadius: 5,
            borderWidth: 1,
            bodySpacing: 5,
            borderColor: "#000",
            callbacks: {
              label: function (tooltipItem, data) {
                let label = `${tooltipItem.formattedValue}`;
                let FormattedLabel = getFormattedLabel(
                  tooltipItem.dataset.label,
                );
                return [`${FormattedLabel}: ${label} €`];
              },
            },
          },
          legend: {
            display: true,
            position: "bottom" as const,
            align: "center" as const,
            labels: {
              usePointStyle: true,
              boxWidth: 0,
              padding: 20,
              pointStyle: "line",
              pointStyleWidth: 30,
              font: {
                size: () => (window.innerWidth < 700 ? 12 : 16),
                family: "Fira Sans",
                color: "rgba(0,0,0)",
              },
            },
          },
          crosshair: {
            sync: {
              enabled: false,
            },
            zoom: {
              enabled: false,
            },
            line: {
              color: "hsla( 10, 1%, 1%, 1 )",
              width: 1,
              dashPattern: [10, 3],
            },
          },
        },
      },
    };
  }

  mounted() {
    Chart.register(...registerables);
    Chart.register(gradient);
    Chart.register(CrosshairPlugin);
    this.createChart(this.canvasID, this.chartConfig);
    //@ts-ignore
    Tooltip.positioners.custom = function (elements, eventPosition) {
      const tooltip = this;
      return {
        x: eventPosition.x,
        y: eventPosition.y,
      };
    };
  }

  get labels() {
    return this.chartDataLabels;
  }

  get mainData() {
    return this.mainChartDataSeries;
  }

  get compareData() {
    return this.comparisonChartDataSeries;
  }

  @Watch("chartDataLabels")
  onChartDataLabels() {
    this.createChart(this.canvasID, this.chartConfig);
  }

  @Watch("mainChartDataSeries")
  onMainChartDataSeries() {
    this.createChart(this.canvasID, this.chartConfig);
  }

  @Watch("comparisonChartDataSeries")
  oncomparisonChartDataSeries() {
    this.createChart(this.canvasID, this.chartConfig);
  }

  createChart(chartId: any, chartData: any) {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    const ctx = document.getElementById(chartId) as HTMLCanvasElement;
    this.chartInstance = new Chart(ctx, {
      type: chartData.type,
      data: chartData.data,
      options: chartData.options,
    });
  }
}
